import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Paperclip, X, UploadCloud, FileVideo } from 'lucide-react';
import { Modal, Button, TextField, Select } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { ticketService } from '../../services/ticketService';
import { useTicketOptions } from './ticketOptions';

const ACCEPT ='image/jpeg,image/png,image/gif,image/webp,video/mp4,video/x-msvideo,video/quicktime,video/webm';

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  const u = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

/**
 * Create-ticket modal. Captures the page it was opened from (source_url /
 * source_label) so support knows where a bug/suggestion was reported, and lets
 * the reporter drag-and-drop screenshots/videos right away.
 */
export default function TicketCreateModal({
  open,
  onClose,
  onCreated,
  serviceName = 'talent-service',
  sourceLabel = '',
}) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const { category: categoryOptions, priority: priorityOptions } = useTicketOptions();
  const [category, setCategory] = useState('bug');
  const [priority, setPriority] = useState('medium');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const addFiles = (list) => {
    if (!list) return;
    setFiles((prev) => [...prev, ...Array.from(list)]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const close = () => {
    reset();
    setCategory('bug');
    setPriority('medium');
    setFiles([]);
    setDragOver(false);
    onClose?.();
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const res = await ticketService.create({
        title: values.title,
        description: values.description,
        category,
        priority,
        service_name: serviceName || null,
        source_url: window.location.pathname + window.location.search,
        source_label: sourceLabel || document.title || null,
      });
      const ticket = res?.data || res;

      if (files.length && ticket?.id) {
        for (const f of files) {
          try {
            await ticketService.uploadAttachment(ticket.id, f);
          } catch {
            toast.error(`Failed to upload ${f.name}`);
          }
        }
      }

      toast.success('Ticket created');
      close();
      onCreated?.(ticket);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="medium"
      showCloseButton
      style={{ padding: 0, backgroundColor: 'var(--palette-background-default)' }}
    >
      <div className="flex max-h-[calc(100dvh-1rem)] min-h-0 flex-col sm:max-h-[85vh]">
        <div className="shrink-0 border-b border-divider px-4 py-4 pr-12 sm:px-6">
          <h2 className="text-lg font-semibold text-fg-primary">New ticket</h2>
          {sourceLabel && (
            <p className="mt-0.5 break-words text-xs text-fg-tertiary">
              From <span className="font-medium text-fg-secondary">{sourceLabel}</span>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
            <TextField
              label="Title"
              fullWidth
              placeholder="Short summary"
              error={!!errors.title}
              helperText={errors.title?.message}
              {...register('title', { required: 'Enter a title' })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={5}
              placeholder="Describe the problem, suggestion or question"
              error={!!errors.description}
              helperText={errors.description?.message}
              {...register('description', { required: 'Enter a description' })}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select label="Category" options={categoryOptions} value={category} onChange={(v) => setCategory(v || 'other')} fullWidth />
              <Select label="Priority" options={priorityOptions} value={priority} onChange={(v) => setPriority(v || 'medium')} fullWidth />
            </div>

            {/* Attachments — drag & drop */}
            <div className="space-y-2">
              <span className="flex items-center gap-1.5 text-sm font-medium text-fg-secondary">
                <Paperclip size={15} /> Attachments
              </span>
              <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => addFiles(e.target.files)} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                className={`flex min-h-28 w-full flex-col items-center justify-center gap-1.5 rounded-ui-lg border-2 border-dashed
                            py-5 text-center transition-colors duration-ui-fast sm:py-6
                            ${dragOver ? 'border-primary bg-primary-overlay' : 'border-divider hover:border-primary/60 hover:bg-action-hover'}`}
              >
                <UploadCloud size={22} className={dragOver ? 'text-primary' : 'text-fg-tertiary'} />
                <span className="text-sm text-fg-secondary">
                  <span className="text-primary font-medium">Click to upload</span> or drag & drop
                </span>
                <span className="text-[11px] text-fg-tertiary">Images & video — up to 10MB / 100MB</span>
              </button>

              {files.length > 0 && (
                <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {files.map((f, i) => {
                    const isImg = f.type.startsWith('image/');
                    return (
                      <li key={`${f.name}-${i}`} className="flex min-w-0 items-center gap-2 bg-surface-default border border-divider rounded-ui-md px-2 py-1.5">
                        {isImg ? (
                          <img src={URL.createObjectURL(f)} alt="" className="h-9 w-9 rounded-ui-sm object-cover shrink-0" />
                        ) : (
                          <span className="grid place-items-center h-9 w-9 rounded-ui-sm bg-action-hover text-fg-tertiary shrink-0">
                            <FileVideo size={16} />
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs text-fg-primary">{f.name}</p>
                          <p className="text-[11px] text-fg-tertiary">{humanSize(f.size)}</p>
                        </div>
                        <button type="button" onClick={() => removeFile(i)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded text-fg-tertiary hover:bg-action-hover hover:text-error" aria-label="Remove">
                          <X size={14} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-divider p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:px-6">
            <Button type="button" variant="outlined" color="neutral" onClick={close} className="w-full sm:w-auto">Cancel</Button>
            <Button type="submit" color="primary" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? 'Creating…' : 'Create ticket'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
